import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { API } from "../../api/api";
import { ACTION_TYPES } from "../../store/actionTypes";
import { FLOW_TYPES } from "../../constants/flowType";
import { VISUAL_HELPERS } from "../../utils";
import { HELPERS } from "../../utils/helpers";
import { useTicketingContext } from "./../../store";
import SummaryView from "./Summary.view";
import SummaryMobile from "./SummaryMobile";

const SummaryContainer = () => {
  const [isCartUpdateInProgress, setIsCartUpdateInProgress] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  const { state, dispatch } = useTicketingContext();
  const {
    summary,
    stepper,
    flowType,
    texts,
    admission_tickets: admissionTickets,
    currencies,
    dates,
    cartMask,
    loginSessionData,
    checkoutUrl: stateCheckoutUrl,
  } = state;

  const {
    selectedDate,
    selectedAddonTickets,
    selectedAdmissionTickets,
    selectedTimeslot,
    selectedGiftCardVariants,
  } = summary;
  const { active: activeStep } = stepper;

  const isGiftCardFlow = flowType === FLOW_TYPES.GIFT_CARD;
  const isLastStep = activeStep === 2 || isGiftCardFlow;
  const isFirstStep = activeStep === 0;

  useEffect(() => {
    let totals;
    if (flowType === FLOW_TYPES.ADMISSION) {
      totals = HELPERS.calculateTotalPricesOnSelectedDate(
        summary,
        dates,
        admissionTickets
      );
    } else if (isGiftCardFlow) {
      totals = HELPERS.calculateTotalPricesForGiftCard(summary);
    } else {
      totals = HELPERS.calculateTotalPricesForActivitiesOnly(state);
    }

    dispatch({
      type: ACTION_TYPES.SET_TOTALS,
      payload: { totals },
    });
  }, [
    selectedAdmissionTickets,
    selectedAddonTickets,
    selectedDate,
    selectedTimeslot,
    selectedGiftCardVariants,
    dates,
  ]);

  useEffect(() => {
    if (isGiftCardFlow) {
      const hasGiftCardVariants =
        !!selectedGiftCardVariants &&
        Object.keys(selectedGiftCardVariants).length > 0;
      dispatch({
        type: ACTION_TYPES.SET_STEP_IS_VALID,
        payload: { step: 0, isValid: hasGiftCardVariants },
      });
      return;
    }
    if (activeStep === 0) {
      const { isStepOneValid, warnings } = HELPERS.stepOneValidityCheck(
        summary,
        admissionTickets
      );
      dispatch({
        type: ACTION_TYPES.SET_STEP_IS_VALID,
        payload: {
          step: 0,
          isValid: isStepOneValid,
        },
      });
      dispatch({
        type: ACTION_TYPES.SET_WARNINGS,
        payload: {
          warnings,
        },
      });
    }
    if (activeStep === 1) {
      const { isStepTwoValid, warnings } = HELPERS.stepTwoValidityCheck(state);
      dispatch({
        type: ACTION_TYPES.SET_STEP_IS_VALID,
        payload: {
          step: 1,
          isValid: isStepTwoValid,
        },
      });
      dispatch({
        type: ACTION_TYPES.SET_WARNINGS,
        payload: {
          warnings,
        },
      });
    }
    if (activeStep === 2) {
      const { isStepThreeValid, warnings } =
        HELPERS.stepThreeValidityCheck(summary);
      dispatch({
        type: ACTION_TYPES.SET_STEP_IS_VALID,
        payload: {
          step: 2,
          isValid: isStepThreeValid,
        },
      });
      dispatch({
        type: ACTION_TYPES.SET_WARNINGS,
        payload: {
          warnings,
        },
      });
    }

    // eslint-disable-next-line
  }, [summary, admissionTickets, activeStep]);

  const syncCartWithState = async () => {
    try {
      const result = await API.syncCart(state);
      dispatch({
        type: ACTION_TYPES.UPDATE_CART,
        payload: result.updatedCart.cart.lines,
      });
      return result;
    } catch (error) {
      console.error("Failed to sync cart:", error);
      throw error;
    }
  };

  const extendReservationAPI = async (cartId) => {
    try {
      await API.extendReservationForCheckout(cartId);
      console.log("Reservation extended successfully");
    } catch (error) {
      console.warn(
        "Reservation extension failed, but continuing to checkout:",
        error
      );
    }
  };

  const onContinueHandler = async () => {
    if (activeStep === 1) {
      setIsCartUpdateInProgress(true);

      syncCartWithState()
        .catch((e) => console.error("Cart sync failed:", e))
        .finally(() => {
          setIsCartUpdateInProgress(false);
        });
    }

    if (isLastStep) {
      setIsCartUpdateInProgress(true);
      try {
        if (!isGiftCardFlow) {
          await syncCartWithState();
        }

        const cartId = localStorage.getItem("cart");
        console.log(cartId);

        if (cartId) {
          await extendReservationAPI(cartId);

          const discountCode = HELPERS.getDiscountCodeFromUrl();
          if (discountCode) {
            try {
              const discountResult = await API.applyDiscount(
                cartId,
                discountCode
              );
              if (discountResult.ok) {
                console.log("Discount applied successfully:", discountCode);
              } else {
                console.warn(
                  "Failed to apply discount:",
                  discountCode,
                  discountResult
                );
              }
            } catch (discountError) {
              console.error("Error applying discount:", discountError);
            }
          }
        } else {
          console.warn("No cart ID found for reservation extension");
        }

        if (stateCheckoutUrl) {
          const checkoutUrl = new URL(stateCheckoutUrl);
          if (checkoutUrl.protocol !== "https:") {
            console.error("Blocked redirect: invalid checkout URL");
            return;
          }
          if (loginSessionData?.loggedIn) {
            checkoutUrl.searchParams.set("logged_in", "true");
          }
          window.location.href = checkoutUrl.toString();
          return;
        } else {
          console.error("No checkout URL found");
        }
      } catch (error) {
        console.error("Failed during checkout preparation:", error);
        return;
      } finally {
        setIsCartUpdateInProgress(false);
      }
      if (state.is_city_card) {
        sessionStorage.setItem(
          "copenhagen_card_data",
          JSON.stringify({
            dateRange: state.summary.CCCardsDateRange,
            selectedCCCards: state.summary.selectedCCCards,
          })
        );
      }
      return;
    }
    dispatch({
      type: ACTION_TYPES.SET_ACTIVE_STEP,
      payload: {
        stepToSet: activeStep + 1,
      },
    });
    const selectedEntranceTimeslot = state.dates
      .find((date) => date.date === state.summary.selectedDate)
      ?.prices[0].timeslots.find(
        (timeslot) => timeslot.entry_no === state.summary.selectedTimeslot
      );
    if (selectedEntranceTimeslot) {
      dispatch({
        type: ACTION_TYPES.ADD_SELECTED_TIMESLOT,
        payload: {
          timeSlot: selectedEntranceTimeslot,
        },
      });
    }
    dispatch({
      type: ACTION_TYPES.CLEAR_ERROR_MESSAGE,
    });
    window.scrollTo(0, 0);
  };

  const onBackHandler = () => {
    if (activeStep === 0) {
      return;
    }
    dispatch({
      type: ACTION_TYPES.SET_ACTIVE_STEP,
      payload: {
        stepToSet: activeStep - 1,
      },
    });
    dispatch({
      type: ACTION_TYPES.CLEAR_ERROR_MESSAGE,
    });
    window.scrollTo(0, 0);
  };

  const onCurrencySelectHandler = (currencyId) => {
    const selectedCurrency = currencies.find(
      (currency) => Number(currency.id) === Number(currencyId)
    );
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_CURRENCY,
      payload: {
        selectedCurrency,
      },
    });
  };

  const summaryViewProps = {
    onBack: onBackHandler,
    onContinue: onContinueHandler,
    admissionTickets,
    isActiveStepValid: HELPERS.isActiveStepValid(stepper),
    onCurrencySelect: onCurrencySelectHandler,
    dateText: VISUAL_HELPERS.displayDateInSummary(
      summary.selectedDate,
      state.texts.common.day_names
    ),
    isSummaryEmpty: HELPERS.isSummaryEmpty(summary),
    totals: state.summary.totals,
    activeStep,
    isFirstStep,
    isLastStep,
    isCartUpdateInProgress,
  };

  if (isMobile) {
    return (
      <div>
        <SummaryMobile {...summaryViewProps} />
      </div>
    );
  }
  return (
    <div>
      <SummaryView {...summaryViewProps} />
    </div>
  );
};

export default SummaryContainer;
